import React, { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { GetTaskExtentionDetailsResDto } from "../../../interfaces/TaskExtentionDtos/taskExtention.model";
import { useAuth } from "../../../firebase/AuthProvider";
import { toast } from "react-toastify";
import { GetTaskExtentionDetailsAsync } from "../../../services/TaskExterntionService/TaskExterntionService";
import Swal from "sweetalert2";
import { approveRejectLeave } from "../../../services/userServices/UserInfoService";
import { showConfirmationDialog } from "../../modal/ConfimationModal/Confimation";
import { useNavigate } from "react-router-dom";
import { AppEvents } from "../../../events/events";

interface Props {
    show: boolean;
    onHide: () => void;
    selectedTaskExtensionRequestId: number;
    onCompleted: () => void;
}

const TaskExtensionModal: React.FC<Props> = ({
    show,
    onHide,
    selectedTaskExtensionRequestId,
    onCompleted
}) => {
    const { currentUser, currentUserInfo } = useAuth();
    const navigate = useNavigate();
    const [taskExtensionDetails, setTaskExtensionDetails] = useState<GetTaskExtentionDetailsResDto>();
    const [rejectReason, setRejectReason] = useState<string>(""); // moved local
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (selectedTaskExtensionRequestId && show) {
            fetchTaskExtensionDetails(selectedTaskExtensionRequestId);
        } else {
            setTaskExtensionDetails(undefined);
        }
    }, [selectedTaskExtensionRequestId, show, currentUser]);

    const fetchTaskExtensionDetails = async (administrativeRequestId: number) => {
        if (!currentUser) return;
        try {
            const token = await currentUser.getIdToken();
            const resp = await GetTaskExtentionDetailsAsync(token, administrativeRequestId);
            if (resp.success) {
                setTaskExtensionDetails(resp.data);
            }
        } catch (err) {
            console.error("Failed to fetch task extension details:", err);
            toast.error("Unexpected error loading details.");
        }
    };

    const handleApprove = async () => {
        if (!currentUser || !selectedTaskExtensionRequestId) return;
        const isConfirm = await showConfirmationDialog(
            "Are you sure?",
            "You are about to approve this Task Extension request.",
        )
        if (!isConfirm) return;

        setLoading(true);
        try {
            const token = await currentUser.getIdToken();
            const response = await approveRejectLeave(selectedTaskExtensionRequestId, "approve", "", token);
            if (response && response.success) {
                AppEvents.emit('UPDATE_ADMIN_REQUEST_COUNT');
                toast.success(response.message);
                onCompleted(); // parent refresh + close
            } else {
                const msg = response.message || "Failed to approve request.";
                toast.error(msg);
                console.error("Approve response:", response);
            }
        } catch (err  : any) {
            console.error("Approve failed:", err);
            toast.error(err?.message || "Unexpected error while approving.");
        } finally {
            setLoading(false);
        }
    };

    const handleReject = async () => {
        if (!currentUser || !selectedTaskExtensionRequestId) return;
        if (!rejectReason.trim()) {
            toast.warning("Please enter a reason for rejection.");
            return;
        }

        const result = await Swal.fire({
            title: "Are you sure?",
            text: "You are about to reject this Task Extension request.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, Reject",
            cancelButtonText: "Cancel",
        });
        if (!result.isConfirmed) return;

        setLoading(true);
        try {
            const token = await currentUser.getIdToken();
            const response = await approveRejectLeave(selectedTaskExtensionRequestId, "reject", rejectReason, token);
            if (response && response.success) {
                AppEvents.emit('UPDATE_ADMIN_REQUEST_COUNT');
                toast.success("Task extension rejected.");
                onCompleted(); // parent refresh + close
            } else {
                const msg = response.message|| "Failed to reject request.";
                toast.error(msg);
                console.error("Reject response:", response);
            }
        } catch (err : any) {
            console.error("Reject failed:", err);
            toast.error(err?.message || "Unexpected error while rejecting.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered size="lg" className="custom_popup">
            <Modal.Header closeButton>
                <Modal.Title>Task Extension Details</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <>
                    <div className="mb-2">
                        <strong>First Attempt Date:</strong>{" "}
                        {taskExtensionDetails?.firstAttemptDate ? new Date(taskExtensionDetails?.firstAttemptDate).toLocaleString() : "N/A"}
                    </div>
                    <div className="mb-2">
                        <strong>Follow Up Count:</strong> {taskExtensionDetails?.followUpCount ?? "N/A"}
                    </div>
                    <div className="mb-2">
                        <strong>Follow Up Methods:</strong>{" "}
                        {(taskExtensionDetails?.followUpMethods && taskExtensionDetails?.followUpMethods.join(", ")) || "N/A"}
                    </div>
                    <div className="mb-2">
                        <strong>Participant Notified:</strong>{" "}
                        {typeof taskExtensionDetails?.participantNotified === "boolean"
                            ? taskExtensionDetails?.participantNotified ? "Yes" : "No"
                            : "N/A"}
                    </div>
                    <div className="mb-2">
                        <strong>Impact On Participant:</strong> {taskExtensionDetails?.impactOnParticipant ?? "N/A"}
                    </div>
                    <div className="mb-2">
                        <strong>Participant Name :</strong> {taskExtensionDetails?.participantName ?? "N/A"}
                    </div>
                    <div className="mb-2">
                        <strong>Task Title:</strong> <span className="text-primary underline cursor-pointer highlight_txt" onClick={() => {
                            if (taskExtensionDetails?.taskId) navigate(
                                `/task/${currentUserInfo?.organization.abbreviation}-${taskExtensionDetails?.taskId}`
                            )
                        }}>
                            {taskExtensionDetails?.taskTitle ?? "N/A"}
                        </span>
                    </div>
                    <div className="mb-2">
                        <strong>Time Needed (days):</strong> {taskExtensionDetails?.timeNeeded ?? "N/A"}
                    </div>
                    <div className="mb-2">
                        <strong>Reason Text:</strong> {taskExtensionDetails?.reasonText ?? "N/A"}
                    </div>

                    <Form.Group className="mt-3">
                        <Form.Label>Rejection Reason (required if rejecting)</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            placeholder="Enter rejection reason..."
                        />
                    </Form.Group>
                </>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Close
                </Button>
                <Button variant="success" onClick={handleApprove} disabled={loading}>
                    Approve
                </Button>
                <Button variant="danger" onClick={handleReject} disabled={loading}>
                    Reject
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default TaskExtensionModal;